/**
 * File Size Constants - Single source of truth for file size limits
 *
 * RULES:
 * 1. ALWAYS use these constants for file size limits
 * 2. NEVER hardcode sizes with `* 1024 * 1024`
 * 3. Use the utility functions for conversions and formatting
 */

// =============================================================================
// UNIT MULTIPLIERS
// =============================================================================

/** 1 Kilobyte in bytes */
export const KB = 1024;

/** 1 Megabyte in bytes */
export const MB = 1024 * 1024;

/** 1 Gigabyte in bytes */
export const GB = 1024 * 1024 * 1024;

// =============================================================================
// FILE SIZE LIMITS
// =============================================================================

export const FILE_SIZE_LIMITS = {
  // ----- Media Files (general) -----
  /** Default limit for media uploads (500 MB) */
  MEDIA_MAX_MB: 500,
  /** Default limit for media uploads in bytes */
  MEDIA_MAX_BYTES: 500 * MB,

  // ----- Video Files -----
  /** Limit for video uploads (500 MB) */
  VIDEO_MAX_MB: 500,
  /** Limit for video uploads in bytes */
  VIDEO_MAX_BYTES: 500 * MB,

  // ----- Audio Files -----
  /** Limit for audio uploads (100 MB) */
  AUDIO_MAX_MB: 100,
  /** Limit for audio uploads in bytes */
  AUDIO_MAX_BYTES: 100 * MB,

  // ----- Voice Cloning -----
  /** Limit for voice cloning samples (50 MB) */
  VOICE_MAX_MB: 50,
  /** Limit for voice cloning samples in bytes */
  VOICE_MAX_BYTES: 50 * MB,

  // ----- Image Files -----
  /** Default limit for image uploads (10 MB) */
  IMAGE_MAX_MB: 10,
  /** Default limit for image uploads in bytes */
  IMAGE_MAX_BYTES: 10 * MB,

  // ----- Thumbnails -----
  /** Limit for thumbnails (10 MB) */
  THUMBNAIL_MAX_MB: 10,
  /** Limit for thumbnails in bytes */
  THUMBNAIL_MAX_BYTES: 10 * MB,

  // ----- Platform-Specific Limits (Thumbnail Studio) -----
  /** YouTube thumbnail max (2 MB) */
  YOUTUBE_THUMBNAIL_MAX_BYTES: 2 * MB,
  /** YouTube banner max (6 MB) */
  YOUTUBE_BANNER_MAX_BYTES: 6 * MB,
  /** Instagram max (30 MB) */
  INSTAGRAM_MAX_BYTES: 30 * MB,
  /** Twitter/X max (5 MB) */
  TWITTER_MAX_BYTES: 5 * MB,
  /** TikTok max (15 MB) */
  TIKTOK_MAX_BYTES: 15 * MB,
  /** LinkedIn post max (5 MB) */
  LINKEDIN_POST_MAX_BYTES: 5 * MB,
  /** LinkedIn banner max (8 MB) */
  LINKEDIN_BANNER_MAX_BYTES: 8 * MB,
  /** Facebook max (30 MB) */
  FACEBOOK_MAX_BYTES: 30 * MB,
  /** Pinterest max (20 MB) */
  PINTEREST_MAX_BYTES: 20 * MB,
  /** Twitch max (10 MB) */
  TWITCH_MAX_BYTES: 10 * MB,
} as const;

// =============================================================================
// CONVERSION UTILITIES
// =============================================================================

/**
 * Converts bytes to megabytes (rounded to 2 decimal places)
 */
export function bytesToMB(bytes: number): number {
  return Math.round((bytes / MB) * 100) / 100;
}

/**
 * Converts megabytes to bytes
 */
export function mbToBytes(mb: number): number {
  return mb * MB;
}

/**
 * Converts bytes to kilobytes (rounded to 2 decimal places)
 */
export function bytesToKB(bytes: number): number {
  return Math.round((bytes / KB) * 100) / 100;
}

/**
 * Converts kilobytes to bytes
 */
export function kbToBytes(kb: number): number {
  return kb * KB;
}

// =============================================================================
// FORMATTING UTILITIES
// =============================================================================

/**
 * Formats bytes to a human-readable string (e.g., "500 MB", "1.2 GB")
 *
 * @param bytes - The size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "1.5 MB" or "500 KB"
 */
export function formatFileSize(bytes: number, decimals = 2): string {
  if (bytes < KB) {
    return `${bytes} B`;
  }
  if (bytes < MB) {
    return `${(bytes / KB).toFixed(decimals)} KB`;
  }
  if (bytes < GB) {
    return `${(bytes / MB).toFixed(decimals)} MB`;
  }
  return `${(bytes / GB).toFixed(decimals)} GB`;
}

/**
 * Formats megabytes to a human-readable string
 *
 * @param mb - The size in megabytes
 * @returns Formatted string like "500 MB" or "1.5 GB"
 */
export function formatMB(mb: number): string {
  return formatFileSize(mb * MB);
}

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Checks if a file size is within the specified limit
 *
 * @param sizeBytes - The file size in bytes
 * @param limitBytes - The maximum allowed size in bytes
 * @returns true if within limit, false otherwise
 */
export function isWithinSizeLimit(sizeBytes: number, limitBytes: number): boolean {
  return sizeBytes <= limitBytes;
}

/**
 * Checks if a file size exceeds the specified MB limit
 *
 * @param sizeBytes - The file size in bytes
 * @param limitMB - The maximum allowed size in megabytes
 * @returns true if exceeds limit, false otherwise
 */
export function exceedsSizeLimitMB(sizeBytes: number, limitMB: number): boolean {
  return sizeBytes > limitMB * MB;
}
